/**
 * Spanish exit-intent popup. Renders only on /es/* routes (App.js gates this).
 *
 * - Desktop: triggers on mouse leaving the top of the viewport
 * - Mobile:  triggers after 60s of idle (no scroll/touch)
 * - Single-session-only via sessionStorage so we don't annoy users
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { t } from '@/i18n/strings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'fwd_es_exit_popup_shown';
const MOBILE_IDLE_MS = 60_000;

const SpanishExitPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(SESSION_KEY)) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    let idleTimer = null;

    const trigger = () => {
      if (window.sessionStorage.getItem(SESSION_KEY)) return;
      window.sessionStorage.setItem(SESSION_KEY, '1');
      setOpen(true);
    };

    const onMouseLeave = (e) => {
      // Only top-of-viewport exits
      if (e.clientY <= 0) trigger();
    };

    const resetIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(trigger, MOBILE_IDLE_MS);
    };

    if (isMobile) {
      resetIdle();
      window.addEventListener('scroll', resetIdle, { passive: true });
      window.addEventListener('touchstart', resetIdle, { passive: true });
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      window.removeEventListener('scroll', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        email,
        source: 'es_exit_popup',
        lang: 'es',
        page: window.location.pathname,
      });
      setDone(true);
      setTimeout(() => setOpen(false), 2500);
    } catch {
      // Silent fail — toast not critical here
      setDone(true);
      setTimeout(() => setOpen(false), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="es-exit-headline"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
      data-testid="es-exit-popup"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          aria-label="Cerrar"
          data-testid="es-exit-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-gradient-to-br from-rose-50 via-amber-50 to-emerald-50 px-6 py-5 border-b border-amber-100">
          <h2
            id="es-exit-headline"
            className="text-2xl font-bold text-gray-900 leading-tight"
          >
            {t('popup_headline', 'es')}
          </h2>
        </div>

        <div className="p-6">
          {done ? (
            <p className="text-emerald-700 font-semibold text-center" data-testid="es-exit-popup-success">
              {t('toast_success', 'es')}
            </p>
          ) : (
            <>
              <p className="text-gray-700 mb-4">{t('popup_subline', 'es')}</p>
              <form onSubmit={submit} className="space-y-3" noValidate>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('form_email', 'es')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="es-exit-popup-email"
                  inputMode="email"
                  autoComplete="email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-md transition-colors"
                  data-testid="es-exit-popup-submit"
                >
                  {submitting ? t('form_submitting', 'es') : t('popup_cta', 'es')}
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-3 text-center">
                {t('popup_reassurance', 'es')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpanishExitPopup;
